"use client";

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { apiFetch } from "../lib/api";
import {
  automaticCheckoutNavigation,
  checkoutCommercialFingerprint,
  checkoutNavigation,
  claimCheckoutIntent,
  clearCheckoutIntent,
  hasCheckoutDraftChanged,
  inspectCheckoutIntent,
  isCheckoutIntentBlocking,
  loadCheckoutIntent,
  postCheckoutWithReconciliation,
  runCheckoutSingleFlight,
  saveCheckoutIntent,
  updateStoredCheckoutIntent,
} from "../lib/checkoutIntent";
import { cartKey, defaultAddress, detectShopCountryFromBrowser, itemRequiresShipping, maxQuantity, primaryImage, toQuoteItem } from "../lib/shop";
import { ShopCartDrawer } from "./ShopCartDrawer";

const STORAGE_KEY = "nitro_shop_cart_v1";
const ShopCartContext = createContext(null);

export function ShopCartProvider({ children }) {
  const [countryIso2, setCountryIso2State] = useState("CO");
  const [cart, setCart] = useState([]);
  const [quote, setQuote] = useState(null);
  const [isReady, setIsReady] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [shippingAddress, setShippingAddress] = useState(defaultAddress("CO"));
  const [selectedPaymentMethodCode, setSelectedPaymentMethodCode] = useState("");
  const [selectedShippingMethodCode, setSelectedShippingMethodCode] = useState("");
  const [state, setState] = useState({ checkingOut: false, error: "", notice: "", intent: null, intentChanged: false });
  const activeIntentKeyRef = useRef(null);
  const casRef = useRef(0);
  const checkoutPromiseRef = useRef(null);
  const inspectionPromiseRef = useRef(null);
  const latestCheckoutRef = useRef(null);
  const loginRedirectedRef = useRef(false);

  useEffect(() => {
    try {
      const saved = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || "{}");
      setCountryIso2State(detectShopCountryFromBrowser());
      if (Array.isArray(saved?.cart)) setCart(saved.cart);
      setShippingAddress(defaultAddress(detectShopCountryFromBrowser()));
    } catch {
      setCountryIso2State(detectShopCountryFromBrowser());
      setCart([]);
    } finally {
      setIsReady(true);
    }
  }, []);

  useEffect(() => {
    if (!isReady) return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ countryIso2, cart }));
    window.dispatchEvent(new CustomEvent("nitro-shop-cart-updated", { detail: { countryIso2, cart } }));
  }, [cart, countryIso2, isReady]);

  useEffect(() => {
    setShippingAddress((current) => ({ ...current, countryIso2 }));
  }, [countryIso2]);

  useEffect(() => {
    let active = true;
    if (!cart.length) {
      setQuote(null);
      return () => {
        active = false;
      };
    }

    apiFetch("/api/v1/shop/quote", {
      method: "POST",
      body: JSON.stringify({
        countryIso2,
        items: cart.map(toQuoteItem),
        paymentMethodCode: selectedPaymentMethodCode || undefined,
        shippingMethodCode: selectedShippingMethodCode || undefined,
      }),
      timeoutMs: 10000,
    })
      .then((data) => {
        if (active) setQuote(data);
      })
      .catch((error) => {
        if (active) setQuote({ ok: false, errors: [error.message], items: [], totals: null, paymentMethods: [] });
      });

    return () => {
      active = false;
    };
  }, [cart, countryIso2, selectedPaymentMethodCode, selectedShippingMethodCode]);

  useEffect(() => {
    if (!quote) return;
    if (!selectedPaymentMethodCode && quote.selectedPaymentMethod?.code) {
      setSelectedPaymentMethodCode(quote.selectedPaymentMethod.code);
    }
    if (!selectedShippingMethodCode && quote.selectedShippingMethod?.code) {
      setSelectedShippingMethodCode(quote.selectedShippingMethod.code);
    }
  }, [quote, selectedPaymentMethodCode, selectedShippingMethodCode]);

  const cartCount = useMemo(() => cart.reduce((total, item) => total + item.quantity, 0), [cart]);
  const selectedPayment = quote?.selectedPaymentMethod || quote?.paymentMethods?.[0] || null;
  const selectedShipping = quote?.selectedShippingMethod || quote?.shippingMethods?.[0] || null;

  latestCheckoutRef.current = {
    cart,
    countryIso2,
    quote,
    selectedPayment,
    selectedPaymentMethodCode,
    selectedShipping,
    selectedShippingMethodCode,
    shippingAddress,
  };

  const setCountryIso2 = useCallback(function setCountryIso2(nextCountry) {
    setCountryIso2State(nextCountry);
  }, []);

  const addProduct = useCallback(function addProduct(product, variant = null, quantity = 1) {
    const key = cartKey(product.slug, variant?.slug || "");
    const safeQuantity = Math.max(1, Math.min(Number(quantity || 1), maxQuantity(variant)));
    setState((current) => ({ ...current, checkingOut: false, error: "", notice: `${product.name}${variant ? ` / ${variant.name}` : ""} agregado al carrito.` }));
    setCart((current) => {
      const existing = current.find((item) => item.key === key);
      if (existing) {
        return current.map((item) => item.key === key ? { ...item, quantity: Math.min(item.quantity + safeQuantity, item.maxQuantity || 9) } : item);
      }
      return [...current, {
        key,
        productSlug: product.slug,
        variantSlug: variant?.slug || "",
        name: product.name,
        variantName: variant?.name || "",
        type: product.type,
        imageUrl: primaryImage(product, variant),
        requiresShipping: itemRequiresShipping(product, variant),
        quantity: safeQuantity,
        maxQuantity: maxQuantity(variant),
      }];
    });
    setIsDrawerOpen(true);
  }, []);

  function updateQuantity(key, quantity) {
    const next = Number(quantity);
    if (!next) {
      setCart((current) => current.filter((item) => item.key !== key));
      return;
    }
    setCart((current) => current.map((item) => item.key === key ? { ...item, quantity: Math.min(Math.max(next, 1), item.maxQuantity || 9) } : item));
  }

  function removeLine(key) {
    setCart((current) => current.filter((item) => item.key !== key));
  }

  function clearCart() {
    setCart([]);
    setQuote(null);
    setState((current) => ({ ...current, checkingOut: false, error: "", notice: "" }));
  }

  async function checkout(options = {}) {
    return runCheckoutSingleFlight(checkoutPromiseRef, () => runCheckout(options));
  }

  async function runCheckout(options) {
    const actorId = options.actorId;
    const payload = checkoutPayload(latestCheckoutRef.current, options.customer);
    setState((current) => ({ ...current, checkingOut: true, error: "", notice: "" }));
    let intent;
    const cas = ++casRef.current;
    try {
      intent = await loadCheckoutIntent(actorId);
      if (cas !== casRef.current) return;
      if (intent) {
        activeIntentKeyRef.current = intent.key;
        const changed = await hasCheckoutDraftChanged(intent, payload);
        if (cas !== casRef.current || activeIntentKeyRef.current !== intent.key) return;
        setState((current) => ({
          ...current,
          checkingOut: false,
          error: changed ? "El carrito o los datos cambiaron después de iniciar la orden. Consulta primero su estado." : "",
          intent: presentStoredIntent(intent),
          intentChanged: changed,
        }));
        if (!changed) await resumeCheckout(actorId, { explicit: true });
        return;
      }

      const claim = await claimCheckoutIntent(actorId, payload, { guard: () => cas === casRef.current });
      if (cas !== casRef.current) return;
      intent = claim.intent;
      activeIntentKeyRef.current = intent.key;
      if (!claim.created) {
        const changed = await hasCheckoutDraftChanged(intent, payload);
        if (cas !== casRef.current || activeIntentKeyRef.current !== intent.key) return;
        setState((current) => ({ ...current, checkingOut: false, intent: presentStoredIntent(intent), intentChanged: changed }));
        if (!changed) await resumeCheckout(actorId, { explicit: true });
        return;
      }
      setState((current) => ({ ...current, checkingOut: true, error: "", notice: "", intent: presentStoredIntent(intent), intentChanged: false }));
      const outcome = await postCheckoutWithReconciliation({ key: intent.key, payload });
      await applyCheckoutOutcome({ actorId, cas, intent, outcome, options, payload });
    } catch (error) {
      await handleCheckoutFailure({ actorId, cas, error, intent });
    }
  }

  async function resumeCheckout(actorId, { explicit = false } = {}) {
    if (!actorId) return null;
    if (inspectionPromiseRef.current) return inspectionPromiseRef.current;
    const operation = runInspection(actorId, explicit);
    inspectionPromiseRef.current = operation;
    try {
      return await operation;
    } finally {
      if (inspectionPromiseRef.current === operation) inspectionPromiseRef.current = null;
    }
  }

  async function runInspection(actorId, explicit) {
    let intent;
    const cas = ++casRef.current;
    try {
      intent = await loadCheckoutIntent(actorId);
      if (cas !== casRef.current) return null;
      if (!intent) return null;
      activeIntentKeyRef.current = intent.key;
      setState((current) => ({ ...current, checkingOut: true, error: "", notice: explicit ? "Consultando la orden existente..." : current.notice, intent: presentStoredIntent(intent) }));
      const outcome = await inspectCheckoutIntent(intent.key);
      await applyCheckoutOutcome({ actorId, cas, intent, outcome, options: {}, payload: null });
      return outcome;
    } catch (error) {
      await handleCheckoutFailure({ actorId, cas, error, intent });
      return null;
    }
  }

  async function applyCheckoutOutcome({ actorId, cas, intent, outcome, options, payload }) {
    if (cas !== casRef.current || activeIntentKeyRef.current !== intent.key) return false;
    const guard = () => cas === casRef.current && activeIntentKeyRef.current === intent.key;
    const stored = await saveCheckoutIntent(actorId, updateStoredCheckoutIntent(intent, outcome), { guard });
    if (!stored) return false;
    if (cas !== casRef.current || activeIntentKeyRef.current !== intent.key) return false;

    const result = outcome.found ? outcome.result : null;
    const presented = {
      ...presentStoredIntent(stored),
      paymentUrl: result?.paymentUrl || null,
      errorCode: result?.errorCode || null,
      canContinue: Boolean(checkoutNavigation(result || stored)),
    };
    setState((current) => ({
      ...current,
      checkingOut: false,
      error: outcome.found ? "" : "No pudimos confirmar si Nitro recibió la orden. No la enviaremos de nuevo.",
      notice: "",
      intent: presented,
      intentChanged: false,
    }));

    if (result?.status === "completed") {
      await clearCheckoutIntent(actorId, intent.key, { guard });
      if (payload && typeof options.getCurrentCustomer === "function") {
        const currentPayload = checkoutPayload(latestCheckoutRef.current, options.getCurrentCustomer());
        const currentFingerprint = await checkoutCommercialFingerprint(currentPayload, intent.key);
        if (currentFingerprint === intent.commercialFingerprint && cas === casRef.current) {
          setCart([]);
          setQuote(null);
        }
      }
    }

    if (outcome.source === "post" && result?.status === "requires_action") {
      const destination = automaticCheckoutNavigation(outcome);
      if (destination && cas === casRef.current) window.location.assign(destination);
      if (!destination) {
        setState((current) => ({ ...current, error: "La orden existe, pero el destino de pago no pudo validarse. Consulta su estado antes de continuar." }));
      }
    }
    return true;
  }

  async function handleCheckoutFailure({ actorId, cas, error, intent }) {
    const guard = () => cas === casRef.current && (!intent || activeIntentKeyRef.current === intent.key);
    if (!guard()) return;
    if (intent && activeIntentKeyRef.current === intent.key) {
      const uncertain = updateStoredCheckoutIntent(intent, { found: false });
      const saved = await saveCheckoutIntent(actorId, uncertain, { guard }).catch(() => null);
      if (!saved || !guard()) return;
      setState((current) => ({ ...current, checkingOut: false, error: checkoutErrorMessage(error), notice: "", intent: presentStoredIntent(uncertain) }));
    } else {
      setState((current) => ({ ...current, checkingOut: false, error: checkoutErrorMessage(error), notice: "" }));
    }
    if (error?.status === 401 && !loginRedirectedRef.current) {
      loginRedirectedRef.current = true;
      window.location.assign(`/login?redirect=${encodeURIComponent("/tienda/carrito")}`);
    }
  }

  function continueCheckoutPayment() {
    const destination = state.intent?.canContinue === true ? checkoutNavigation(state.intent) : null;
    if (!destination) {
      setState((current) => ({ ...current, error: "No pudimos validar el destino de pago. Consulta de nuevo el estado de la orden." }));
      return;
    }
    window.location.assign(destination);
  }

  async function startNewCheckoutIntent(actorId) {
    const intent = state.intent;
    if (!intent || intent.status !== "failed") return false;
    if (!await clearCheckoutIntent(actorId, intent.key)) return false;
    ++casRef.current;
    activeIntentKeyRef.current = null;
    setState((current) => ({ ...current, checkingOut: false, error: "", notice: "Puedes iniciar un intento nuevo cuando estés listo.", intent: null, intentChanged: false }));
    return true;
  }

  async function reviewCheckoutDraft(actorId, customer) {
    if (!actorId) return false;
    try {
      const intent = await loadCheckoutIntent(actorId);
      if (!intent) return false;
      const changed = await hasCheckoutDraftChanged(intent, checkoutPayload(latestCheckoutRef.current, customer));
      if (activeIntentKeyRef.current && activeIntentKeyRef.current !== intent.key) return false;
      setState((current) => current.intent?.key === intent.key
        ? { ...current, intentChanged: changed }
        : current);
      return changed;
    } catch {
      return false;
    }
  }

  const value = useMemo(() => ({
    cart,
    cartCount,
    checkoutBlocked: isCheckoutIntentBlocking(state.intent),
    checkout,
    clearCart,
    countryIso2,
    isDrawerOpen,
    isReady,
    openCart: () => setIsDrawerOpen(true),
    closeCart: () => setIsDrawerOpen(false),
    addProduct,
    quote,
    reviewCheckoutDraft,
    resumeCheckout,
    removeLine,
    selectedPayment,
    selectedPaymentMethodCode,
    selectedShipping,
    selectedShippingMethodCode,
    setCountryIso2,
    setSelectedPaymentMethodCode,
    setSelectedShippingMethodCode,
    shippingAddress,
    setShippingAddress,
    state,
    continueCheckoutPayment,
    startNewCheckoutIntent,
    updateQuantity,
  }), [addProduct, cart, cartCount, countryIso2, isDrawerOpen, isReady, quote, selectedPayment, selectedPaymentMethodCode, selectedShipping, selectedShippingMethodCode, setCountryIso2, shippingAddress, state]);

  return (
    <ShopCartContext.Provider value={value}>
      {children}
      <ShopCartDrawer />
    </ShopCartContext.Provider>
  );
}

function checkoutPayload(snapshot, customer) {
  return {
    countryIso2: snapshot?.countryIso2,
    items: (snapshot?.cart || []).map(toQuoteItem),
    paymentMethodCode: snapshot?.selectedPaymentMethodCode || snapshot?.selectedPayment?.code || undefined,
    shippingMethodCode: snapshot?.selectedShippingMethodCode || snapshot?.selectedShipping?.code || undefined,
    shippingAddress: snapshot?.quote?.requiresShipping ? snapshot.shippingAddress : null,
    customer: customer || undefined,
  };
}

function presentStoredIntent(intent) {
  return intent ? {
    key: intent.key,
    status: intent.status,
    provider: intent.provider,
    orderNumber: intent.orderNumber,
    paymentUrl: null,
    canContinue: Boolean(checkoutNavigation(intent)),
  } : null;
}

function checkoutErrorMessage(error) {
  if (error?.status === 401) return "Tu sesión terminó. Conservamos la identidad de la compra para consultarla al volver.";
  if (error?.status === 409) return "La identidad de esta compra no coincide con el carrito actual. Consulta la orden antes de continuar.";
  if (error?.code === "CHECKOUT_INTENT_STORAGE_UNAVAILABLE") return error.message;
  return error?.message || "No fue posible confirmar la orden. Conservamos su identidad y no la enviaremos de nuevo.";
}

export function useShopCart() {
  const context = useContext(ShopCartContext);
  if (!context) throw new Error("useShopCart debe usarse dentro de ShopCartProvider.");
  return context;
}
